import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import color from "../../../Constants/colors";
import { DocumentCraftingUser } from "../../../Models/documentCrafting";
import { useDocumentCraftingMembers } from "../../../Query/useDocumentCrafting";
import { displayName } from "../Common/format";
import { useDebounced } from "./useDebounced";

/**
 * "Created by": a workspace member, searched on the server
 * (`GET /members/?q=`). `knownUsers` (people on the current page) name a
 * member that came in through a link before the member list has loaded.
 */
const MemberFilter: React.FC<{
  value: number | null;
  onChange: (userId: number | null) => void;
  knownUsers: (DocumentCraftingUser | null)[];
}> = ({ value, onChange, knownUsers }) => {
  const { t } = useTranslation("documentCrafting");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<DocumentCraftingUser | null>(null);
  const updateQuery = useDebounced((text: string) => setQuery(text.trim()), 300);

  // Also loaded for a preset value, to name it.
  const members = useDocumentCraftingMembers(query, open || value !== null);

  const selected = useMemo<DocumentCraftingUser | null>(() => {
    if (value === null) return null;
    if (picked?.id === value) return picked;
    const candidates = [...(members.data?.members ?? []), ...knownUsers];
    const match = candidates.find((user) => user?.id === value);
    return match ?? { id: value, name: `#${value}`, surname: "", email: "" };
  }, [value, picked, members.data, knownUsers]);

  const options = useMemo(() => {
    const list = members.data?.members ?? [];
    return selected && !list.some((user) => user.id === selected.id)
      ? [selected, ...list]
      : list;
  }, [members.data, selected]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selected}
      loading={members.isFetching}
      // The server filters by name and e-mail.
      filterOptions={(list) => list}
      getOptionLabel={(user) => displayName(user)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, user) => {
        setPicked(user);
        onChange(user ? user.id : null);
      }}
      onInputChange={(_, text, reason) => {
        if (reason === "input") updateQuery(text);
        if (reason === "clear") updateQuery("");
      }}
      noOptionsText={t("list.filters.noMembers")}
      loadingText={t("list.filters.loading")}
      renderOption={(props, user) => {
        const { key, ...optionProps } =
          props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <Box component="li" key={key} {...optionProps}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: color.gray900 }}>
                {displayName(user)}
              </Typography>
              {user.email && (
                <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("list.filters.createdBy")}
          placeholder={t("list.filters.anyone")}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  );
};

export default MemberFilter;
